// ProjectDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import ReactMarkdown from 'react-markdown';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { 
  FolderOpen, Kanban, GitBranch, Bot, Brain, Lightbulb, Cpu, 
  ChevronDown, Eye, Users, Image as ImageIcon 
} from 'lucide-react';

/**
 * KanbanColumn - Internal component for rendering a kanban column
 */
const KanbanColumn = ({ 
  title, 
  tasks, 
  bgColor, 
  dotColor, 
  borderColor,
  onTaskClick,
  onViewDeliverable 
}) => (
  <div className={`${bgColor} rounded-lg p-3`}>
    <div className="flex items-center gap-2 mb-3">
      <div className={`h-2 w-2 rounded-full ${dotColor}`}></div>
      <h4 className="font-medium text-sm">{title}</h4>
      <Badge variant="outline" className="ml-auto">{tasks?.length || 0}</Badge>
    </div>
    <div className="space-y-2">
      {(tasks || []).map(task => (
        <div 
          key={task.id} 
          className={`bg-white p-3 rounded-lg border shadow-sm ${borderColor} hover:shadow-md cursor-pointer transition-all`}
          onClick={() => onTaskClick(task)}
          data-testid={`kanban-task-${task.id}`}
        >
          <p className="text-sm font-medium line-clamp-2">{task.title}</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <Bot className="h-3 w-3" />
            <span>{task.agent_name || 'Unassigned'}</span>
          </div>
          {task.has_deliverables && (
            <div 
              className="mt-2 flex items-center gap-1"
              onClick={(e) => {
                e.stopPropagation();
                onViewDeliverable && onViewDeliverable(task);
              }}
            >
              <Badge className="bg-teal-100 text-teal-700 text-xs hover:bg-teal-200 cursor-pointer">
                <Eye className="h-3 w-3 mr-1" />
                {task.deliverables_count} deliverable{task.deliverables_count > 1 ? 's' : ''}
              </Badge>
            </div>
          )}
          {task.output_summary && (
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{task.output_summary}</p>
          )}
        </div>
      ))}
      {(tasks?.length || 0) === 0 && (
        <p className="text-xs text-muted-foreground text-center py-4">No tasks</p>
      )}
    </div>
  </div>
);

/**
 * Dialog for viewing project details with Kanban and Mind Map views
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.project - Project data
 * @param {Object} props.projectKanban - Kanban board data with tasks by status
 * @param {string} props.viewMode - Current view mode ('kanban' or 'mindmap')
 * @param {function} props.onViewModeChange - Callback when view mode changes
 * @param {function} props.onTaskClick - Callback when a task is clicked (receives task)
 * @param {function} props.onViewDeliverable - Callback when deliverable badge is clicked
 * @param {function} props.onClose - Callback when close is clicked
 * @param {function} props.onViewDeliverables - Callback to navigate to gallery tab
 */
export const ProjectDialog = ({
  open,
  onOpenChange,
  project,
  projectKanban,
  viewMode = 'kanban',
  onViewModeChange,
  onTaskClick,
  onViewDeliverable,
  onClose,
  onViewDeliverables
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-emerald-500" />
            {project?.title || 'Project Details'}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-4">
            <span>Lead: {project?.lead_agent || 'AI Cortex'}</span>
            <span>•</span>
            <span>{project?.task_count || 0} tasks</span>
            <span>•</span>
            <Badge className={
              project?.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
              project?.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
              'bg-slate-100 text-slate-700'
            }>
              {project?.status?.replace('_', ' ') || 'active'}
            </Badge>
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-6">
          {/* View Tabs: Kanban and Mind Map */}
          <div className="flex gap-2 border-b pb-2">
            <Button 
              variant="ghost" 
              className={`flex items-center gap-2 rounded-none ${viewMode === 'kanban' ? 'border-b-2 border-emerald-500' : 'text-muted-foreground'}`}
              onClick={() => onViewModeChange('kanban')}
              data-testid="project-view-kanban"
            >
              <Kanban className="h-4 w-4" />
              Kanban View
            </Button>
            <Button 
              variant="ghost" 
              className={`flex items-center gap-2 rounded-none ${viewMode === 'mindmap' ? 'border-b-2 border-emerald-500' : 'text-muted-foreground'}`}
              onClick={() => onViewModeChange('mindmap')}
              data-testid="project-view-mindmap"
            >
              <GitBranch className="h-4 w-4" />
              Mind Map
            </Button>
          </div>
          
          {/* Kanban Board */}
          {viewMode === 'kanban' && projectKanban && (
            <div className="grid grid-cols-5 gap-4">
              <KanbanColumn 
                title="Backlog"
                tasks={projectKanban.kanban?.backlog}
                bgColor="bg-slate-50"
                dotColor="bg-slate-400"
                borderColor=""
                onTaskClick={onTaskClick}
                onViewDeliverable={onViewDeliverable}
              />
              <KanbanColumn 
                title="In Progress"
                tasks={projectKanban.kanban?.in_progress}
                bgColor="bg-amber-50"
                dotColor="bg-amber-500"
                borderColor="border-amber-200"
                onTaskClick={onTaskClick}
                onViewDeliverable={onViewDeliverable}
              />
              <KanbanColumn 
                title="Review"
                tasks={projectKanban.kanban?.review}
                bgColor="bg-purple-50"
                dotColor="bg-purple-500"
                borderColor="border-purple-200"
                onTaskClick={onTaskClick}
                onViewDeliverable={onViewDeliverable}
              />
              <KanbanColumn 
                title="Completed"
                tasks={projectKanban.kanban?.completed}
                bgColor="bg-emerald-50"
                dotColor="bg-emerald-500"
                borderColor="border-emerald-200"
                onTaskClick={onTaskClick}
                onViewDeliverable={onViewDeliverable}
              />
              <KanbanColumn 
                title="Failed"
                tasks={projectKanban.kanban?.failed}
                bgColor="bg-red-50"
                dotColor="bg-red-500"
                borderColor="border-red-200"
                onTaskClick={onTaskClick}
                onViewDeliverable={onViewDeliverable}
              />
            </div>
          )}
          
          {/* Mind Map View */}
          {viewMode === 'mindmap' && projectKanban && (
            <div className="py-4">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 min-h-[400px]">
                {/* Central Node - Project */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    {/* Project Node */}
                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-xl p-4 shadow-lg min-w-[200px] text-center z-10 relative">
                      <FolderOpen className="h-6 w-6 mx-auto mb-2" />
                      <h4 className="font-semibold">{project?.title || 'Project'}</h4>
                      <p className="text-xs text-emerald-100 mt-1">{project?.task_count || 0} tasks</p>
                    </div>
                    
                    {/* Connection Lines */}
                    <div className="absolute top-full left-1/2 w-px h-8 bg-gradient-to-b from-emerald-400 to-transparent"></div>
                  </div>
                  
                  {/* Cortex Node */}
                  <div className="mt-8 relative">
                    <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-xl p-4 shadow-lg min-w-[180px] text-center">
                      <Cpu className="h-5 w-5 mx-auto mb-2" />
                      <h4 className="font-medium text-sm">Cortex (Synthesis)</h4>
                      <p className="text-xs text-purple-100 mt-1">Gemini 3 Pro</p>
                    </div>
                    
                    {/* Branch lines */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 flex gap-20 mt-4">
                      <div className="w-px h-12 bg-gradient-to-b from-blue-400 to-transparent -ml-10"></div>
                      <div className="w-px h-12 bg-gradient-to-b from-pink-400 to-transparent ml-10"></div>
                    </div>
                  </div>
                  
                  {/* Brain Nodes Row */}
                  <div className="flex gap-16 mt-16">
                    {/* Left Brain */}
                    <div className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white rounded-xl p-4 shadow-lg min-w-[160px] text-center">
                      <Brain className="h-5 w-5 mx-auto mb-2" />
                      <h4 className="font-medium text-sm">Left Brain</h4>
                      <p className="text-xs text-blue-100 mt-1">Analytical</p>
                      <Badge className="mt-2 bg-blue-400/30 text-white text-xs">GPT-4o</Badge>
                    </div>
                    
                    {/* Right Brain */}
                    <div className="bg-gradient-to-br from-pink-500 to-rose-600 text-white rounded-xl p-4 shadow-lg min-w-[160px] text-center">
                      <Lightbulb className="h-5 w-5 mx-auto mb-2" />
                      <h4 className="font-medium text-sm">Right Brain</h4>
                      <p className="text-xs text-pink-100 mt-1">Creative</p>
                      <Badge className="mt-2 bg-pink-400/30 text-white text-xs">Claude 4.5</Badge>
                    </div>
                  </div>
                  
                  {/* Agent Nodes */}
                  <div className="mt-12 w-full">
                    <h4 className="text-center text-sm font-medium text-muted-foreground mb-4">Agents Involved</h4>
                    <div className="flex flex-wrap justify-center gap-4">
                      {projectKanban.agents?.map((agent, idx) => (
                        <div 
                          key={idx}
                          className="bg-white rounded-lg p-3 shadow-md border-2 border-slate-200 hover:border-emerald-300 cursor-pointer transition-all min-w-[140px]"
                          onClick={() => {
                            // Find a task by this agent and show its detail
                            const allTasks = [
                              ...(projectKanban.kanban?.completed || []),
                              ...(projectKanban.kanban?.in_progress || []),
                              ...(projectKanban.kanban?.backlog || [])
                            ];
                            const agentTask = allTasks.find(t => t.agent_name === agent.name);
                            if (agentTask) {
                              onTaskClick(agentTask);
                            }
                          }}
                          data-testid={`mindmap-agent-${idx}`}
                        >
                          <div 
                            className="h-10 w-10 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: agent.avatar_color || '#6366F1' }}
                          >
                            {agent.name?.charAt(0) || 'A'}
                          </div>
                          <p className="text-sm font-medium text-center">{agent.name}</p>
                          <p className="text-xs text-muted-foreground text-center">{agent.task_count || 0} tasks</p>
                        </div>
                      ))}
                      {(!projectKanban.agents || projectKanban.agents.length === 0) && (
                        <p className="text-sm text-muted-foreground">No agents assigned yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* AI Reasoning Summary */}
          {project?.cortex_summary && (
            <Card className="border-emerald-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Brain className="h-4 w-4 text-emerald-600" />
                  AI Reasoning Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                    <Cpu className="h-4 w-4" />
                    Cortex Synthesis
                    <ChevronDown className="h-4 w-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2 pl-6">
                    <div className="prose prose-sm max-w-none text-muted-foreground">
                      <ReactMarkdown>{project.cortex_summary}</ReactMarkdown>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                
                {project?.left_brain_summary && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-blue-700">
                      <Brain className="h-4 w-4" />
                      Left Brain (Analytical)
                      <ChevronDown className="h-4 w-4" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 pl-6">
                      <p className="text-sm text-muted-foreground">{project.left_brain_summary}</p>
                    </CollapsibleContent>
                  </Collapsible>
                )}
                
                {project?.right_brain_summary && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-purple-700">
                      <Lightbulb className="h-4 w-4" />
                      Right Brain (Creative)
                      <ChevronDown className="h-4 w-4" />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 pl-6">
                      <p className="text-sm text-muted-foreground">{project.right_brain_summary}</p>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Agents Involved */}
          {project?.agents_involved?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Agents Involved
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {project.agents_involved.map((agent, idx) => (
                    <Badge 
                      key={idx} 
                      className={agent.role === 'lead' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}
                    >
                      <Bot className="h-3 w-3 mr-1" />
                      {agent.name}
                      {agent.role === 'lead' && <span className="ml-1 text-xs">(Lead)</span>}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose} data-testid="project-dialog-close">
            Close
          </Button>
          <Button onClick={onViewDeliverables} data-testid="project-view-deliverables">
            <ImageIcon className="h-4 w-4 mr-2" />
            View Deliverables
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDialog;
